import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type DependencyList,
  type ReactNode,
} from "react";

/**
 * Client-side data loading for pages.
 *
 * `useLoader(load, deps)` runs `load` on mount, whenever `deps` change, and
 * whenever someone calls `useRevalidate()()` (the SPA equivalent of
 * re-rendering the page with fresh data after a mutation).
 */

const RevalidateContext = createContext<{ version: number; bump: () => void }>({
  version: 0,
  bump: () => undefined,
});

export function RevalidateProvider({ children }: { children: ReactNode }) {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  return (
    <RevalidateContext.Provider value={{ version, bump }}>{children}</RevalidateContext.Provider>
  );
}

/** Returns a function that makes every mounted `useLoader` refetch. */
export function useRevalidate(): () => void {
  return useContext(RevalidateContext).bump;
}

export type LoaderState<T> =
  | { status: "loading"; data: T | undefined; error: null }
  | { status: "ready"; data: T; error: null }
  | { status: "error"; data: T | undefined; error: unknown };

export function useLoader<T>(load: () => Promise<T>, deps: DependencyList): LoaderState<T> {
  const { version } = useContext(RevalidateContext);
  const [state, setState] = useState<LoaderState<T>>({
    status: "loading",
    data: undefined,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ status: "loading", data: prev.data, error: null }));
    load().then(
      (data) => {
        if (!cancelled) setState({ status: "ready", data, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setState((prev) => ({ status: "error", data: prev.data, error }));
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version]);

  return state;
}

/** Centered placeholder while a page's first load is in flight. */
export function PageLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
      Loading…
    </div>
  );
}

/** Inline error panel for a failed page load. */
export function PageError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="mx-auto my-10 max-w-lg rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300">
      {message}
    </div>
  );
}
